/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { get, set } from 'idb-keyval';
import { motion, AnimatePresence } from 'motion/react';
import { Album, UrlItem, SyncSettings, SyncStatus } from './types';
import { migrateAlbums } from './lib/migrations';
import { syncWithCloud } from './lib/sync';
import { mergeAlbums, sanitizeAlbumCover } from './lib/merge';
import { SyncSettingsModal } from './components/SyncSettingsModal';
import { ToastContainer, ToastMessage, ToastType } from './components/Toast';
import { SplashScreen } from './components/SplashScreen';

import * as LuIcons from 'react-icons/lu';
import SafeIcon from './common/SafeIcon';

const { 
  LuCopy, LuTrash2, LuImage, LuLink, LuCheckCircle, LuSearch, LuFolder, 
  LuPlus, LuArrowLeft, LuFolderOpen, LuExternalLink, LuClipboardList, 
  LuDownload, LuLayoutGrid, LuCloud, LuCloudOff, LuRefreshCw, LuStar, LuCheck,
  LuChevronLeft, LuChevronRight, LuX, LuMaximize2, LuFilm
} = LuIcons;

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function extractUrls(text: string): string[] {
  const matches = text.match(URL_REGEX);
  return matches ? Array.from(new Set(matches)) : [];
}

/**
 * Detects if a URL points to a supported hosted video file.
 * Checks for extensions: .mp4, .webm, .ogg, .mov, .m4v (case-insensitive)
 * Handles URLs with query parameters or hash fragments.
 */
function isVideoUrl(url: string): boolean {
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.m4v'];
    return videoExtensions.some(ext => pathname.endsWith(ext));
  } catch {
    // Fallback regex check if URL parsing fails
    const videoRegex = /\.(mp4|webm|ogg|mov|m4v)(\?.*|#.*)?$/i;
    return videoRegex.test(url);
  }
}

const DEFAULT_SETTINGS: SyncSettings = {
  scriptUrl: '',
  deviceLabel: 'My Device',
  autoSync: false
};

/**
 * UX CONSISTENCY: Album media are intentionally sorted newest first.
 */
function sortUrlsNewestFirst(items: UrlItem[]): UrlItem[] {
  return [...items].sort((a, b) => {
    const timeA = a.updatedAt || a.createdAt || 0;
    const timeB = b.updatedAt || b.createdAt || 0;
    return timeB - timeA;
  });
}

export default function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [currentAlbumId, setCurrentAlbumId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [gridSize, setGridSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [copiedAll, setCopiedAll] = useState(false);
  
  // Lightbox State
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const [syncSettings, setSyncSettings] = useState<SyncSettings>(DEFAULT_SETTINGS);
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [deviceId, setDeviceId] = useState<string>('');
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const albumsRef = useRef<Album[]>([]);
  const syncSettingsRef = useRef<SyncSettings>(DEFAULT_SETTINGS);
  const deviceIdRef = useRef<string>('');
  const isSyncingRef = useRef(false);
  const hasQueuedSyncRef = useRef(false);
  const syncSequenceRef = useRef(0);
  const lastAppliedSyncSequenceRef = useRef(0);
  const isLoadedRef = useRef(false);

  useEffect(() => { albumsRef.current = albums; }, [albums]);
  useEffect(() => { syncSettingsRef.current = syncSettings; }, [syncSettings]);
  useEffect(() => { deviceIdRef.current = deviceId; }, [deviceId]);
  useEffect(() => { isLoadedRef.current = isLoaded; }, [isLoaded]);

  const addToast = useCallback((type: ToastType, message: string) => {
    setToasts(prev => [...prev, { id: crypto.randomUUID(), type, message }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const [newAlbumName, setNewAlbumName] = useState('');
  const [isCreatingAlbum, setIsCreatingAlbum] = useState(false);
  const [inputText, setInputText] = useState('');

  const performSync = useCallback(async (reason: 'manual' | 'startup' | 'interval' | 'focus' | 'online' | 'queued' = 'interval') => {
    if (!isLoadedRef.current) return;
    if (!syncSettingsRef.current.scriptUrl) {
      if (reason === 'manual') addToast('error', 'Sync URL not configured');
      setSyncStatus('not_configured');
      return;
    }

    if (isSyncingRef.current) {
      hasQueuedSyncRef.current = true;
      return;
    }

    if (!navigator.onLine) {
      setSyncStatus('offline');
      if (reason === 'manual') addToast('error', 'You are offline');
      return;
    }

    isSyncingRef.current = true;
    setSyncStatus('syncing');
    
    const currentSequence = ++syncSequenceRef.current;

    try {
      const { remoteAlbums, serverTime } = await syncWithCloud(
        albumsRef.current, 
        syncSettingsRef.current, 
        deviceIdRef.current
      );

      if (currentSequence < lastAppliedSyncSequenceRef.current) return;

      setAlbums(currentLocal => {
        const merged = mergeAlbums(currentLocal, remoteAlbums);
        return merged;
      });

      lastAppliedSyncSequenceRef.current = currentSequence;
      setLastSyncAt(serverTime);
      setSyncStatus('success');
      
      if (reason === 'manual') addToast('success', 'Sync completed successfully');
    } catch (err: any) {
      setSyncStatus('error');
      if (reason === 'manual') addToast('error', err.message || 'Sync failed');
    } finally {
      isSyncingRef.current = false;
      if (hasQueuedSyncRef.current) {
        hasQueuedSyncRef.current = false;
        setTimeout(() => performSync('queued'), 100);
      }
    }
  }, [addToast]);

  useEffect(() => {
    async function loadData() {
      const hasSeenSplash = sessionStorage.getItem('linklens-splash-seen');
      if (hasSeenSplash) setShowSplash(false);

      let id = await get('linklens-device-id');
      if (!id) {
        id = crypto.randomUUID();
        await set('linklens-device-id', id);
      }
      setDeviceId(id);

      const savedSettings = await get('linklens-sync-settings');
      if (savedSettings) {
        setSyncSettings(savedSettings);
        if (!savedSettings.scriptUrl) setSyncStatus('not_configured');
      } else {
        setSyncStatus('not_configured');
      }

      const savedLastSync = await get('linklens-last-sync');
      if (savedLastSync) setLastSyncAt(savedLastSync);

      const val = await get('linklens-albums');
      if (val && Array.isArray(val)) {
        setAlbums(migrateAlbums(val, id));
      }

      setIsLoaded(true);
      isLoadedRef.current = true;
    }
    loadData();
  }, []);

  useEffect(() => { if (isLoaded) set('linklens-albums', albums); }, [albums, isLoaded]);
  useEffect(() => { if (isLoaded) set('linklens-sync-settings', syncSettings); }, [syncSettings, isLoaded]);
  useEffect(() => { if (isLoaded && lastSyncAt !== null) set('linklens-last-sync', lastSyncAt); }, [lastSyncAt, isLoaded]);

  useEffect(() => {
    if (isLoaded && syncSettings.scriptUrl) {
      performSync('startup');
    }
  }, [isLoaded, performSync, syncSettings.scriptUrl]);

  useEffect(() => {
    if (!isLoaded || !syncSettings.scriptUrl) return;
    const interval = setInterval(() => performSync('interval'), 10 * 60 * 1000);
    let focusTimeout: any;
    const handleFocus = () => {
      clearTimeout(focusTimeout);
      focusTimeout = setTimeout(() => performSync('focus'), 500);
    };
    const handleOnline = () => performSync('online');
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    return () => {
      clearInterval(interval);
      clearTimeout(focusTimeout);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, [isLoaded, syncSettings.scriptUrl, performSync]);

  const handleCreateAlbum = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAlbumName.trim()) return;
    const now = Date.now();
    const newAlbum: Album = {
      id: crypto.randomUUID(),
      name: newAlbumName.trim(),
      urls: [],
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      deviceId,
      syncVersion: 0
    };
    setAlbums(prev => [newAlbum, ...prev]);
    setNewAlbumName('');
    setIsCreatingAlbum(false);
  };

  const handleDeleteAlbum = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this album and all its media?')) {
      const now = Date.now();
      setAlbums(prev => prev.map(a => 
        a.id === id ? { ...a, deletedAt: now, updatedAt: now } : a
      ));
      if (currentAlbumId === id) setCurrentAlbumId(null);
    }
  };

  const handleSetCover = (albumId: string, itemId: string) => {
    const now = Date.now();
    setAlbums(prev => prev.map(album => {
      if (album.id === albumId) {
        return { ...album, coverItemId: itemId, updatedAt: now };
      }
      return album;
    }));
    addToast('success', 'Album cover updated');
  };

  const activeAlbums = useMemo(() => albums.filter(a => !a.deletedAt), [albums]);
  const currentAlbum = useMemo(() => activeAlbums.find(a => a.id === currentAlbumId), [activeAlbums, currentAlbumId]);

  const handleAddUrls = () => {
    if (!currentAlbumId) return;
    const newUrls = extractUrls(inputText);
    if (newUrls.length > 0) {
      const now = Date.now();
      setAlbums(prev => prev.map(album => {
        if (album.id === currentAlbumId) {
          const existingActiveUrls = new Set(
            album.urls.filter(u => !u.deletedAt).map(u => u.url)
          );

          const itemsToAdd: UrlItem[] = newUrls
            .filter(url => !existingActiveUrls.has(url))
            .map(url => ({
              id: crypto.randomUUID(),
              url,
              createdAt: now,
              updatedAt: now,
              deletedAt: null
            }));

          if (itemsToAdd.length === 0) return album;

          return { 
            ...album, 
            urls: [...itemsToAdd, ...album.urls], 
            updatedAt: now 
          };
        }
        return album;
      }));
      setInputText('');
      addToast('success', `Added ${newUrls.length} media link(s)`);
    }
  };

  const handleRemoveUrl = (itemId: string) => {
    if (!currentAlbumId) return;
    const now = Date.now();
    setAlbums(prev => prev.map(album => {
      if (album.id === currentAlbumId) {
        const updatedAlbum = {
          ...album,
          updatedAt: now,
          urls: album.urls.map(u => u.id === itemId ? { ...u, deletedAt: now, updatedAt: now } : u)
        };
        updatedAlbum.coverItemId = sanitizeAlbumCover(updatedAlbum);
        return updatedAlbum;
      }
      return album;
    }));
    if (lightboxIndex !== null) setLightboxIndex(null);
  };

  const handleClearAllUrls = () => {
    if (!currentAlbumId) return;
    if (window.confirm('Are you sure you want to clear all media in this album?')) {
      const now = Date.now();
      setAlbums(prev => prev.map(album => {
        if (album.id === currentAlbumId) {
          return {
            ...album,
            updatedAt: now,
            coverItemId: null,
            urls: album.urls.map(u => ({ ...u, deletedAt: now, updatedAt: now }))
          };
        }
        return album;
      }));
      setLightboxIndex(null);
    }
  };

  const handleCopyAllUrls = async () => {
    if (!currentAlbum) return;
    const activeUrls = sortUrlsNewestFirst(
      currentAlbum.urls.filter(u => !u.deletedAt)
    ).map(u => u.url);

    if (activeUrls.length === 0) return;
    try {
      await navigator.clipboard.writeText(activeUrls.join('\n'));
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (err) { console.error('Copy failed', err); }
  };

  const handleExportUrls = () => {
    if (!currentAlbum) return;
    const activeUrls = sortUrlsNewestFirst(
      currentAlbum.urls.filter(u => !u.deletedAt)
    ).map(u => u.url);

    if (activeUrls.length === 0) return;
    const blob = new Blob([activeUrls.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentAlbum.name.replace(/\s+/g, '_')}_links.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const cycleGridSize = () => {
    setGridSize(prev => prev === 'small' ? 'medium' : prev === 'medium' ? 'large' : 'small');
  };

  const filteredAlbums = useMemo(() => {
    if (!searchQuery) return activeAlbums;
    return activeAlbums.filter(a => a.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [activeAlbums, searchQuery]);

  const filteredUrls = useMemo(() => {
    if (!currentAlbum) return [];
    const activeUrls = currentAlbum.urls.filter(u => !u.deletedAt);
    const sorted = sortUrlsNewestFirst(activeUrls);
    
    if (!searchQuery) return sorted;
    return sorted.filter(u => u.url.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [currentAlbum, searchQuery]);

  const handleSplashComplete = () => {
    setShowSplash(false);
    sessionStorage.setItem('linklens-splash-seen', 'true');
  };

  if (!isLoaded) return null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-blue-200">
      <AnimatePresence mode="wait">
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      </AnimatePresence>

      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 shrink-0 cursor-pointer" onClick={() => { setCurrentAlbumId(null); setSearchQuery(''); }}>
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-inner">
              <SafeIcon icon={LuSearch} className="w-6 h-6 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold tracking-tight text-slate-900 leading-tight">LinkLens</h1>
              <p className="text-xs text-slate-500 font-medium">Instant Media Previews</p>
            </div>
          </div>
          
          <div className="flex-1 max-w-md relative">
            <SafeIcon icon={LuSearch} className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder={currentAlbumId ? "Search media..." : "Search albums..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-100 border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl outline-none transition-all text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            {currentAlbumId && currentAlbum?.urls.filter(u => !u.deletedAt).length ? (
              <button
                onClick={handleClearAllUrls}
                className="text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors shrink-0"
              >
                Clear All
              </button>
            ) : <div className="w-[70px]"></div>}

            <button
              onClick={() => setIsSyncModalOpen(true)}
              className="p-2 text-slate-500 hover:bg-slate-100 rounded-xl transition-colors relative"
              title="Sync Settings"
            >
              {syncStatus === 'syncing' ? (
                <SafeIcon icon={LuRefreshCw} className="w-5 h-5 animate-spin text-blue-600" />
              ) : syncStatus === 'success' ? (
                <SafeIcon icon={LuCloud} className="w-5 h-5 text-green-600" />
              ) : syncStatus === 'error' || syncStatus === 'offline' ? (
                <SafeIcon icon={LuCloudOff} className="w-5 h-5 text-red-500" />
              ) : (
                <SafeIcon icon={LuCloud} className="w-5 h-5" />
              )}
              {syncStatus === 'error' && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!currentAlbumId ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-800">Your Albums</h2>
              <button
                onClick={() => setIsCreatingAlbum(true)}
                className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all flex items-center gap-2 shadow-sm text-sm"
              >
                <SafeIcon icon={LuPlus} className="w-4 h-4" />
                New Album
              </button>
            </div>

            {isCreatingAlbum && (
              <form onSubmit={handleCreateAlbum} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex gap-3 items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                  <SafeIcon icon={LuFolder} className="w-5 h-5 text-blue-600" />
                </div>
                <input
                  type="text"
                  autoFocus
                  placeholder="Album Name"
                  value={newAlbumName}
                  onChange={(e) => setNewAlbumName(e.target.value)}
                  className="flex-1 bg-transparent border-none focus:ring-0 text-lg font-semibold outline-none placeholder:text-slate-300"
                />
                <button type="button" onClick={() => setIsCreatingAlbum(false)} className="px-4 py-2 text-slate-500 hover:bg-slate-100 rounded-lg text-sm font-medium transition-colors">Cancel</button>
                <button type="submit" disabled={!newAlbumName.trim()} className="px-4 py-2 bg-slate-900 text-white disabled:bg-slate-300 rounded-lg text-sm font-medium transition-colors">Create</button>
              </form>
            )}

            {filteredAlbums.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                <AnimatePresence>
                  {filteredAlbums.map(album => (
                    <AlbumCard 
                      key={album.id} 
                      album={album} 
                      onClick={() => { setCurrentAlbumId(album.id); setSearchQuery(''); }}
                      onDelete={(e) => handleDeleteAlbum(album.id, e)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              !isCreatingAlbum && (
                <div className="text-center py-20 px-4 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={LuFolder} className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-1">No albums found</h3>
                  <p className="text-slate-500 max-w-md mx-auto mb-6">
                    {searchQuery ? "Try a different search term." : "Create your first album to start saving media previews."}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => setIsCreatingAlbum(true)}
                      className="px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-sm inline-flex items-center gap-2"
                    >
                      <SafeIcon icon={LuPlus} className="w-5 h-5" />
                      Create Album
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        ) : (
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => { setCurrentAlbumId(null); setSearchQuery(''); }}
                  className="w-10 h-10 flex items-center justify-center bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm text-slate-600"
                >
                  <SafeIcon icon={LuArrowLeft} className="w-5 h-5" />
                </button>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                    {currentAlbum?.name}
                  </h2>
                  <p className="text-sm text-slate-500 font-medium">{currentAlbum?.urls.filter(u => !u.deletedAt).length || 0} items</p>
                </div>
              </div>

              <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
                <button
                  onClick={cycleGridSize}
                  className="px-3 py-2 bg-white border border-slate-200 text-slate-600 font-medium rounded-xl hover:bg-slate-50 transition-all shadow-sm flex items-center gap-2 text-sm whitespace-nowrap"
                  title="Toggle Grid Size"
                >
                  <SafeIcon icon={LuLayoutGrid} className="w-4 h-4" />
                  <span className="hidden sm:inline">Grid: {gridSize.charAt(0).toUpperCase() + gridSize.slice(1)}</span>
                </button>
                <button
                  onClick={handleCopyAllUrls}
                  className="px-3 py-2 bg-white border border-slate-200 text-slate-600 font-medium rounded-xl hover:bg-slate-50 transition-all shadow-sm flex items-center gap-2 text-sm whitespace-nowrap"
                >
                  {copiedAll ? <SafeIcon icon={LuCheckCircle} className="w-4 h-4 text-green-500" /> : <SafeIcon icon={LuClipboardList} className="w-4 h-4" />}
                  <span className="hidden sm:inline">{copiedAll ? 'Copied All!' : 'Copy All Links'}</span>
                </button>
                <button
                  onClick={handleExportUrls}
                  className="px-3 py-2 bg-white border border-slate-200 text-slate-600 font-medium rounded-xl hover:bg-slate-50 transition-all shadow-sm flex items-center gap-2 text-sm whitespace-nowrap"
                >
                  <SafeIcon icon={LuDownload} className="w-4 h-4" />
                  <span className="hidden sm:inline">Export .txt</span>
                </button>
              </div>
            </div>

            <section className="bg-white p-5 sm:p-6 rounded-2xl shadow-sm border border-slate-200">
              <label htmlFor="url-input" className="block text-sm font-semibold text-slate-700 mb-2">
                Add links to this album
              </label>
              <textarea
                id="url-input"
                className="w-full h-24 p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y font-mono text-sm transition-all"
                placeholder="https://example.com/image1.jpg&#10;https://example.com/video.mp4"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
              />
              <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="text-sm font-medium text-slate-500 bg-slate-100 px-3 py-1.5 rounded-lg">
                  <span className={extractUrls(inputText).length > 0 ? "text-blue-600 font-bold" : ""}>{extractUrls(inputText).length}</span> links detected
                </div>
                <button
                  onClick={handleAddUrls}
                  disabled={extractUrls(inputText).length === 0}
                  className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all flex items-center justify-center gap-2 shadow-sm"
                >
                  <SafeIcon icon={LuLink} className="w-4 h-4" />
                  Add to Album
                </button>
              </div>
            </section>

            {filteredUrls.length > 0 ? (
              <section>
                <div className={`grid gap-4 sm:gap-6 ${
                  gridSize === 'small' ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10' :
                  gridSize === 'medium' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6' :
                  'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4'
                }`}>
                  <AnimatePresence>
                    {filteredUrls.map((item, index) => (
                      <ImageCard 
                        key={item.id} 
                        item={item} 
                        isCover={currentAlbum?.coverItemId === item.id}
                        onRemove={() => handleRemoveUrl(item.id)} 
                        onSetCover={() => handleSetCover(currentAlbum!.id, item.id)}
                        onView={() => setLightboxIndex(index)}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </section>
            ) : (
              <section className="text-center py-20 px-4 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={LuImage} className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-1">No media found</h3>
                <p className="text-slate-500 max-w-md mx-auto">
                  {searchQuery ? "Try a different search term." : "Paste some URLs in the box above to add media to this album."}
                </p>
              </section>
            )}
          </div>
        )}
      </main>

      <SyncSettingsModal
        isOpen={isSyncModalOpen}
        onClose={() => setIsSyncModalOpen(false)}
        settings={syncSettings}
        lastSyncAt={lastSyncAt}
        onSave={(newSettings) => {
          setSyncSettings(newSettings);
          addToast('success', 'Settings saved');
        }}
        status={syncStatus}
        onManualSync={() => performSync('manual')}
        albums={albums}
        onImport={(importedAlbums, importedSettings) => {
          const merged = mergeAlbums(albums, importedAlbums);
          setAlbums(merged);
          if (importedSettings) setSyncSettings(importedSettings);
          addToast('success', 'Backup imported successfully');
        }}
      />

      <ImageLightbox
        images={filteredUrls}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(newIndex) => setLightboxIndex(newIndex)}
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

function ImageLightbox({ 
  images, 
  currentIndex, 
  onClose, 
  onNavigate 
}: { 
  images: UrlItem[]; 
  currentIndex: number | null; 
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const isOpen = currentIndex !== null;
  const currentImage = currentIndex !== null ? images[currentIndex] : null;
  const isVideo = currentImage ? isVideoUrl(currentImage.url) : false;

  const next = useCallback(() => {
    if (currentIndex === null) return;
    onNavigate((currentIndex + 1) % images.length);
  }, [currentIndex, images.length, onNavigate]);

  const prev = useCallback(() => {
    if (currentIndex === null) return;
    onNavigate((currentIndex - 1 + images.length) % images.length);
  }, [currentIndex, images.length, onNavigate]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeys = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeys);
    return () => window.removeEventListener('keydown', handleKeys);
  }, [isOpen, next, prev, onClose]);

  if (!isOpen || !currentImage) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/95 backdrop-blur-md p-4 sm:p-8">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full h-full flex flex-col items-center justify-center max-w-6xl mx-auto"
      >
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 z-20">
          <div className="bg-black/50 px-4 py-2 rounded-xl border border-white/10 backdrop-blur-sm">
            <p className="text-white text-sm font-bold tracking-tight">
              {currentIndex + 1} <span className="text-white/40 mx-1">/</span> {images.length}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-xl border border-white/10 transition-colors backdrop-blur-sm"
          >
            <SafeIcon icon={LuX} className="w-6 h-6" />
          </button>
        </div>

        <div className="relative w-full h-full flex items-center justify-center p-4">
          <AnimatePresence mode="wait">
            {isVideo ? (
              <motion.video
                key={currentImage.id}
                src={currentImage.url}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="max-w-full max-h-full object-contain shadow-2xl rounded-lg"
              />
            ) : (
              <motion.img
                key={currentImage.id}
                src={currentImage.url}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="max-w-full max-h-full object-contain shadow-2xl rounded-lg"
                alt="Full view"
              />
            )}
          </AnimatePresence>

          <button 
            onClick={prev}
            className="absolute left-0 sm:-left-8 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full border border-white/10 transition-all backdrop-blur-sm group"
          >
            <SafeIcon icon={LuChevronLeft} className="w-8 h-8 group-hover:-translate-x-1 transition-transform" />
          </button>

          <button 
            onClick={next}
            className="absolute right-0 sm:-right-8 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full border border-white/10 transition-all backdrop-blur-sm group"
          >
            <SafeIcon icon={LuChevronRight} className="w-8 h-8 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        <div className="mt-8 flex items-center gap-4 w-full justify-center">
          <div className="bg-white/10 px-6 py-3 rounded-2xl border border-white/10 backdrop-blur-sm flex items-center gap-4 max-w-md w-full">
            <div className="truncate text-xs text-white/60 font-mono flex-1">{currentImage.url}</div>
            <button 
              onClick={() => navigator.clipboard.writeText(currentImage.url)}
              className="p-2 hover:bg-white/10 text-white/80 rounded-lg transition-colors"
              title="Copy URL"
            >
              <SafeIcon icon={LuCopy} className="w-4 h-4" />
            </button>
            <a 
              href={currentImage.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 hover:bg-white/10 text-white/80 rounded-lg transition-colors"
              title="Open Original"
            >
              <SafeIcon icon={LuExternalLink} className="w-4 h-4" />
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function AlbumCard({ album, onClick, onDelete }: { album: Album; onClick: () => void; onDelete: (e: React.MouseEvent) => void }) {
  const activeUrls = useMemo(() => album.urls.filter(u => !u.deletedAt), [album.urls]);
  const coverUrl = useMemo(() => {
    if (album.coverItemId) {
      const cover = activeUrls.find(u => u.id === album.coverItemId);
      if (cover) return cover.url;
    }
    if (activeUrls.length > 0) {
      const sorted = sortUrlsNewestFirst(activeUrls);
      return sorted[0].url;
    }
    return null;
  }, [album.coverItemId, activeUrls]);
  const isVideoCover = coverUrl ? isVideoUrl(coverUrl) : false;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      onClick={onClick}
      className="bg-white rounded-2xl shadow-sm border border-slate-200 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group flex flex-col overflow-hidden"
    >
      <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden flex items-center justify-center">
        {coverUrl ? (
          isVideoCover ? (
            <video
              src={coverUrl}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 pointer-events-none"
            />
          ) : (
            <img src={coverUrl} alt={album.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          )
        ) : (
          <div className="flex flex-col items-center text-slate-300">
            <SafeIcon icon={LuFolderOpen} className="w-10 h-10 mb-2" />
            <span className="text-xs font-bold uppercase tracking-widest">Empty</span>
          </div>
        )}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onDelete} className="p-2 bg-white/90 hover:bg-red-500 text-slate-400 hover:text-white rounded-xl shadow-sm transition-all">
            <SafeIcon icon={LuTrash2} className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-lg font-bold text-slate-800 truncate">{album.name}</h3>
        <p className="text-sm text-slate-500 font-medium">{activeUrls.length} items</p>
      </div>
    </motion.div>
  );
}

function ImageCard({ 
  item, 
  isCover, 
  onRemove, 
  onSetCover, 
  onView 
}: { 
  item: UrlItem; 
  isCover: boolean; 
  onRemove: () => void; 
  onSetCover: () => void;
  onView: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);
  const isVideo = isVideoUrl(item.url);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(item.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { console.error('Copy failed', err); }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className={`group relative rounded-xl bg-white shadow-sm border ${isCover ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200'} flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}
    >
      <div className="relative aspect-square overflow-hidden bg-slate-100 rounded-t-xl flex items-center justify-center cursor-pointer" onClick={onView}>
        {!error ? (
          isVideo ? (
            <video
              src={item.url}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              onError={() => setError(true)}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 pointer-events-none"
            />
          ) : (
            <img src={item.url} alt="Preview" loading="lazy" onError={() => setError(true)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
          )
        ) : (
          <div className="flex flex-col items-center text-slate-400 p-4 text-center">
            <SafeIcon icon={LuImage} className="w-8 h-8 mb-2 opacity-50" />
            <span className="text-xs font-medium">Preview unavailable</span>
          </div>
        )}
        
        {isCover && (
          <div className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1 shadow-lg z-10">
            <SafeIcon icon={LuStar} className="w-3 h-3" /> Cover
          </div>
        )}

        {isVideo && (
          <div className="absolute bottom-2 left-2 bg-slate-900/80 text-white px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-lg z-10">
            <SafeIcon icon={LuFilm} className="w-3 h-3" /> Video
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 top-1/2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4 backdrop-blur-[1px] pointer-events-none">
          <div className="bg-white text-slate-900 px-4 py-2 rounded-lg font-bold text-xs flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-all shadow-lg leading-none">
            <SafeIcon icon={LuMaximize2} className="w-3 h-3" />
            Click to View
          </div>
        </div>

        <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all z-20">
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="p-2 bg-white/90 hover:bg-red-500 text-slate-600 hover:text-white rounded-lg shadow-sm" title="Delete">
            <SafeIcon icon={LuTrash2} className="w-4 h-4" />
          </button>
          {!isCover && (
            <button onClick={(e) => { e.stopPropagation(); onSetCover(); }} className="p-2 bg-white/90 hover:bg-blue-600 text-slate-600 hover:text-white rounded-lg shadow-sm" title="Set as Cover">
              <SafeIcon icon={LuStar} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      <div className="p-2.5 flex items-center justify-between bg-white rounded-b-xl z-20 relative border-t border-slate-100">
        <div className="truncate text-[10px] font-bold text-slate-400 flex-1 mr-2 uppercase tracking-wide" title={item.url}>{item.url.replace(/^https?:\/\//, '')}</div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={handleCopy} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Copy Link">
            {copied ? <SafeIcon icon={LuCheck} className="w-3.5 h-3.5 text-green-500" /> : <SafeIcon icon={LuCopy} className="w-3.5 h-3.5" />}
          </button>
          <a href={item.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Open in new tab"><SafeIcon icon={LuExternalLink} className="w-3.5 h-3.5" /></a>
        </div>
      </div>
    </motion.div>
  );
}